import { useParams, Link, useNavigate } from "react-router-dom";
import { useState, useMemo } from "react";
import SEOHead from "@/components/feature/SEOHead";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/base/ArticleCard";
import { articles, categories } from "@/mocks/articles";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || categories[0]?.slug || "");

  const activeCategory = useMemo(
    () => categories.find((c) => c.slug === activeSlug),
    [activeSlug]
  );

  const categoryArticles = useMemo(
    () => articles.filter((a) => a.categorySlug === activeSlug),
    [activeSlug]
  );

  const navigate = useNavigate();

  const handleCategoryChange = (newSlug: string) => {
    setActiveSlug(newSlug);
    window.scrollTo({ top: 0, behavior: "smooth" });
    navigate("/category/" + newSlug, { replace: true });
  };

  const siteUrl = typeof window !== "undefined" ? window.location.origin + __BASE_PATH__ : "";

  if (!activeCategory) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <p className="text-foreground-600">分类未找到</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <SEOHead
        title={`${activeCategory.name} - 韩国女主播深度解析 | K-Stream 解析`}
        description={activeCategory.description}
        keywords={`韩国女主播,${activeCategory.name},韩国直播文化,韩国女主播直播平台`}
        canonicalUrl={siteUrl + "/category/" + activeCategory.slug}
        breadcrumbs={[
          { name: "首页", url: siteUrl + "/" },
          { name: activeCategory.name, url: siteUrl + "/category/" + activeCategory.slug },
        ]}
      />

      {/* Header */}
      <section className="pt-28 pb-10 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb />
          <div className="text-center max-w-2xl mx-auto">
            <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mb-4">
              <i className={`${activeCategory.icon} text-2xl`}></i>
            </div>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
              {activeCategory.name}
            </h1>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
              {activeCategory.description}
            </p>
          </div>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="bg-background-50 border-b border-background-200/70 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-2 overflow-x-auto py-3 scrollbar-hide flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategoryChange(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeSlug === cat.slug
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200/70"
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles */}
      <section className="py-10 md:py-14 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                <i className="ri-file-list-3-line text-2xl"></i>
              </div>
              <p className="text-foreground-600 text-sm">该分类暂无文章，敬请期待。</p>
            </div>
          )}
        </div>
      </section>

      {/* Related Categories */}
      <section className="py-10 md:py-14 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-6 text-center">
            探索更多分类
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {categories
              .filter((c) => c.slug !== activeSlug)
              .map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className="flex items-center gap-2 px-4 py-3 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-500 transition-colors no-underline cursor-pointer"
                >
                  <i className={`${cat.icon} text-base`}></i>
                  <span className="whitespace-nowrap">{cat.name}</span>
                </button>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
}